// components/ChatWidget.jsx - Mobile Responsive Version
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  TextField,
  Button,
  Avatar,
  Badge,
  CircularProgress,
  Fade,
  Slide,
  Alert,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  Fab,
  Zoom
} from '@mui/material';
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Minimize as MinimizeIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  SupportAgent as SupportIcon,
  SmartToy as AiIcon,
  Check as CheckIcon,
  DoneAll as DoneAllIcon,
  Psychology as PsychologyIcon,
  RestaurantMenu as MenuIcon,
  LocalOffer as OfferIcon,
  AccessTime as TimeIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon
} from '@mui/icons-material';
import { useChat } from '../context/ChatContext';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const ChatWidget = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const {
    socket,
    isConnected,
    chatOpen,
    minimized,
    currentChat,
    messages,
    unreadCount,
    isTyping,
    loading,
    userInfo,
    messagesEndRef,
    setUserInfo,
    startChat,
    sendMessage,
    sendTyping,
    markAsRead,
    closeChat,
    toggleChat,
    toggleMinimize,
    setChatOpen,
    onlinePlayers,
    pendingGameInvitation,
    registerGameEventHandlers,
    unregisterGameEventHandlers,
    sendGameInvitation,
    acceptGameInvitation,
    rejectGameInvitation,
    sendGameMove,
    requestGameRematch,
    getOnlinePlayers,
    registerPlayerReady,
    clearPendingInvitation,
    setMessages,
    addAIMessage
  } = useChat();
  
  const currentTableId = localStorage.getItem('tableId') || 0;
  const messagesContainerRef = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const restaurantId = localStorage.getItem('current_rest_id_now') || 0;

  const [newMessage, setNewMessage] = useState('');
  const [showStartForm, setShowStartForm] = useState(!currentChat);
  const [formData, setFormData] = useState({
    name: userInfo?.name || user?.username || '',
    email: userInfo?.email || user?.email || '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [gameOpen, setGameOpen] = useState(false);
  const [hasAccepted, setHasAccepted] = useState(false);
  
  // NEW: AI Assistant states
  const [chatMode, setChatMode] = useState('ai');
  const [aiThinking, setAiThinking] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiContext, setAiContext] = useState(null);

  // Suggested questions for AI
  const suggestedQuestions = {
    ar: [
      'ما هي الأطباق النباتية؟',
      'هل هناك عروض خاصة اليوم؟',
      'ما هو سعر البيتزا؟',
      'أخبرني عن الحلويات',
      'ما هي ساعات العمل؟',
      'أوصي بطبق'
    ],
    en: [
      'What vegetarian dishes do you have?',
      'Any special offers today?',
      'How much is the pizza?',
      'Tell me about desserts',
      'What are your opening hours?',
      'Recommend a dish'
    ]
  };

  // Handle scroll to show/hide scroll button
  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      setShowScrollButton(scrollHeight - scrollTop - clientHeight > 100);
    }
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Update form data when userInfo changes
  useEffect(() => {
    if (userInfo) {
      setFormData(prev => ({
        ...prev,
        name: userInfo.name || prev.name || user?.username || '',
        email: userInfo.email || prev.email || user?.email || ''
      }));
    }
  }, [userInfo, user]);

  // Update showStartForm when currentChat changes
  useEffect(() => {
    if (currentChat) {
      setShowStartForm(false);
    } else {
      setShowStartForm(true);
    }
  }, [currentChat]);

  // Reset acceptance flag when new invitation arrives
  useEffect(() => {
    if (pendingGameInvitation) {
      setHasAccepted(false);
    }
  }, [pendingGameInvitation]);

  // Mark messages as read when chat is opened
  useEffect(() => {
    if (chatOpen && !minimized && currentChat) {
      markAsRead();
    }
  }, [chatOpen, minimized, currentChat, markAsRead]);

  // Load AI suggestions based on context
  useEffect(() => {
    if (chatMode === 'ai' && !showStartForm) {
      loadAiSuggestions();
    }
  }, [chatMode, showStartForm]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (!minimized && chatOpen) {
      scrollToBottom();
    }
  }, [messages, minimized, chatOpen]);

  const loadAiSuggestions = async () => {
    try {
      const response = await api.get('/ai/suggestions');
      setAiSuggestions(response.data.suggestions);
    } catch (error) {
      console.error('Error loading AI suggestions:', error);
    }
  };

  const handleStartChat = useCallback(async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert(language === 'ar' ? 'الرجاء إدخال الاسم' : 'Please enter your name');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await startChat(formData.name, formData.email, formData.message);
    } catch (error) {
      console.error('Error starting chat:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, language, startChat]);

  const handleSendMessage = useCallback(async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    if (chatMode === 'ai') {
      await handleAIChat(newMessage);
    } else {
      sendMessage(newMessage);
    }
    setNewMessage('');
  }, [newMessage, chatMode, sendMessage]);


const handleAIChat = async (message) => {
  setAiThinking(true);
  
  // Check if it's an admin command based on keywords
  const adminCommandPatterns = [
    /update price/i,
    /change price/i,
    /set price/i,
    /modify price/i,
    /discount/i,
    /خصم/i,
    /سعر/i,
    /stock/i,
    /inventory/i,
    /quantity/i,
    /enable|disable/i,
    /activate|deactivate/i,
    /تفعيل|إلغاء/i,
    /add product/i,
    /new product/i,
    /remove product/i,
    /delete product/i,
    /إضافة منتج/i,
    /حذف منتج/i,
    /تعديل سعر/i,
    /تحديث المخزون/i
  ];
  
  const isAdminCommand = adminCommandPatterns.some(pattern => pattern.test(message));
  
  try {
    let response;
    
    if (isAdminCommand && (user?.role === 'admin' || user?.role === 'super')) {
      // Use admin command endpoint
      response = await api.post('/ai/admin-command', {
        message,
        restaurantId,
        conversationHistory: messages,
        language,
        userRole: user?.role
      });
      
      // Show a different indicator for admin actions
      addAIMessage(response.data.response, 'admin-action');
      
    } else {
      // Regular AI chat for customers
      response = await api.post('/ai/chat', {
        message,
        restaurantId,
        conversationHistory: messages,
        language
      });
      
      addAIMessage(response.data.response);
      setAiContext(response.data.context);
    }
    
  } catch (error) {
    console.error('Error in AI chat:', error);
    
    let errorMessageText = language === 'ar' 
      ? 'عذراً، حدث خطأ. الرجاء المحاولة مرة أخرى.'
      : 'Sorry, something went wrong. Please try again.';
    
    // Check for specific error types
    if (error.response?.status === 403) {
      errorMessageText = language === 'ar'
        ? 'غير مصرح لك بتنفيذ هذا الأمر. يرجى تسجيل الدخول كمسؤول.'
        : 'Unauthorized to execute this command. Please login as admin.';
    } else if (error.response?.data?.message) {
      errorMessageText = error.response.data.message;
    }
    
    const errorMessage = {
      id: Date.now(),
      message: errorMessageText,
      sender_type: 'ai',
      created_at: new Date().toISOString(),
      is_read: true,
      isError: true
    };
    setMessages(prev => [...prev, errorMessage]);
  } finally {
    setAiThinking(false);
  }
};

  const handleTyping = useCallback((e) => {
    const value = e.target.value;
    setNewMessage(value);
    if (chatMode === 'human') {
      sendTyping(value.length > 0);
    }
  }, [sendTyping, chatMode]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const acceptInvitationHandler = async () => {
    if (!pendingGameInvitation || hasAccepted) return;
    setHasAccepted(true);
    
    try {
      await acceptGameInvitation(
        pendingGameInvitation.gameId,
        pendingGameInvitation.fromTable,
        currentTableId,
        `Table ${currentTableId}`
      );
      clearPendingInvitation();
      setGameOpen(true);
    } catch (error) {
      console.error('Error accepting invitation:', error);
      setHasAccepted(false);
    }
  };

  const rejectInvitationHandler = () => {
    if (!pendingGameInvitation || hasAccepted) return;
    rejectGameInvitation(pendingGameInvitation.gameId, currentTableId);
    clearPendingInvitation();
  };

  const handleGameClose = () => {
    setGameOpen(false);
    setHasAccepted(false);
    if (getOnlinePlayers) getOnlinePlayers();
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setChatMode(newMode);
      if (currentChat && newMode === 'ai') {
        const welcomeMessage = {
          id: Date.now(),
          message: language === 'ar' 
            ? 'مرحباً! أنا مساعد QR Table الذكي. كيف يمكنني مساعدتك اليوم؟ يمكنك سؤالي عن القائمة، العروض، أو أي شيء عن المطعم! 🍽️'
            : 'Hi! I\'m QR Table\'s smart assistant. How can I help you today? Ask me about the menu, offers, or anything about the restaurant! 🍽️',
          sender_type: 'ai',
          created_at: new Date().toISOString(),
          is_read: true
        };
        setMessages([welcomeMessage]);
      }
    }
  };

  if (!socket) return null;

  // Mobile-specific chat button position (adjusts for bottom navigation)
  const buttonBottom = isMobile ? 80 : 90;

  return (
    <>
      {/* Floating Chat Button - Mobile Optimized */}
      <Zoom in={!chatOpen} timeout={300}>
        <Fab
          onClick={toggleChat}
          sx={{
            position: 'fixed',
            bottom: buttonBottom,
            right: isMobile ? 16 : 20,
            zIndex: 1300,
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            width: isMobile ? 56 : 60,
            height: isMobile ? 56 : 60,
            '&:hover': {
              transform: 'scale(1.1)',
              transition: 'transform 0.2s ease'
            },
            boxShadow: '0 4px 20px rgba(255,215,0,0.4)'
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: '#FF3D00',
                color: '#FFFFFF',
                fontWeight: 'bold',
                right: isMobile ? 2 : 5,
                top: isMobile ? 2 : 5
              }
            }}
          >
            {chatMode === 'ai' ? (
              <PsychologyIcon sx={{ color: '#000000', fontSize: isMobile ? 28 : 30 }} />
            ) : (
              <ChatIcon sx={{ color: '#000000', fontSize: isMobile ? 28 : 30 }} />
            )}
          </Badge>
        </Fab>
      </Zoom>

      {/* Chat Drawer - Mobile uses full-screen drawer, Desktop uses popup */}
      {isMobile ? (
        <SwipeableDrawer
          anchor="bottom"
          open={chatOpen}
          onClose={toggleChat}
          onOpen={() => {}}
          swipeAreaWidth={60}
          disableSwipeToOpen={false}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              borderTopLeftRadius: '24px',
              borderTopRightRadius: '24px',
              height: minimized ? '80px' : '85vh',
              maxHeight: '85vh',
              backgroundColor: '#0D0D0D',
              backgroundImage: 'none',
              overflow: 'hidden',
              transition: 'height 0.3s ease'
            }
          }}
        >
          {/* Mobile Chat Header */}
          <Box
            sx={{
              p: 2,
              background: 'linear-gradient(135deg, #FFD700 0%, #F5C800 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderTopLeftRadius: '24px',
              borderTopRightRadius: '24px',
              cursor: 'pointer'
            }}
            onClick={toggleMinimize}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: '#000000', width: 40, height: 40 }}>
                {chatMode === 'ai' ? (
                  <PsychologyIcon sx={{ color: '#FFD700', fontSize: 24 }} />
                ) : (
                  <SupportIcon sx={{ color: '#FFD700', fontSize: 24 }} />
                )}
              </Avatar>
              <Box>
                <Typography sx={{ color: '#000000', fontWeight: 800, fontSize: '1rem' }}>
                  {chatMode === 'ai' 
                    ? (language === 'ar' ? 'مساعد QR Table الذكي' : 'QR Table AI')
                    : (language === 'ar' ? 'الدعم الفني' : 'Support')}
                </Typography>
                <Typography sx={{ color: '#000000', fontSize: '0.65rem', opacity: 0.8 }}>
                  {chatMode === 'ai' 
                    ? (language === 'ar' ? '🤖 متاح 24/7' : '🤖 24/7')
                    : (isConnected ? '🟢 متصل' : '🔴 غير متصل')}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!minimized && (
                <ToggleButtonGroup
                  size="small"
                  value={chatMode}
                  exclusive
                  onChange={handleModeChange}
                  sx={{
                    '& .MuiToggleButton-root': {
                      backgroundColor: '#0D0D0D',
                      color: '#000000',
                      border: 'none',
                      padding: '4px 8px',
                      '&.Mui-selected': {
                        backgroundColor: '#000000',
                        color: '#FFD700'
                      }
                    }
                  }}
                >
                  <ToggleButton value="human">
                    <SupportIcon sx={{ fontSize: 16 }} />
                  </ToggleButton>
                  <ToggleButton value="ai">
                    <PsychologyIcon sx={{ fontSize: 16 }} />
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleMinimize();
                }}
                sx={{ color: '#000000' }}
              >
                {minimized ? <ExpandLessIcon /> : <MinimizeIcon />}
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleChat();
                }}
                sx={{ color: '#000000' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {!minimized && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Rest of the chat content - same as desktop version */}
              {/* Start Chat Form */}
              {showStartForm && (
                <Box
                  component="form"
                  onSubmit={handleStartChat}
                  sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto' }}
                >
                  <Typography sx={{ color: '#FFD700', textAlign: 'center', fontSize: '0.9rem' }}>
                    {chatMode === 'ai'
                      ? (language === 'ar' ? 'اسألني عن القائمة والعروض!' : 'Ask me about menu and offers!')
                      : (language === 'ar' ? 'كيف يمكننا مساعدتك؟' : 'How can we help you?')}
                  </Typography>
                  
                  <TextField
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'الاسم' : 'Name'}
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                    disabled={!!user || isSubmitting}
                    InputProps={{
                      sx: {
                        color: '#FFFFFF',
                        backgroundColor: '#1A1A1A',
                        borderRadius: '12px',
                        '& fieldset': { borderColor: '#2D2D2D' }
                      }
                    }}
                    InputLabelProps={{ sx: { color: '#B3B3B3' } }}
                  />
                  
                  <TextField
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    disabled={!!user || isSubmitting}
                    InputProps={{
                      sx: {
                        color: '#FFFFFF',
                        backgroundColor: '#1A1A1A',
                        borderRadius: '12px',
                        '& fieldset': { borderColor: '#2D2D2D' }
                      }
                    }}
                    InputLabelProps={{ sx: { color: '#B3B3B3' } }}
                  />
                  
                  {chatMode === 'human' && (
                    <TextField
                      fullWidth
                      size="small"
                      label={language === 'ar' ? 'الرسالة (اختياري)' : 'Message (optional)'}
                      multiline
                      rows={3}
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      disabled={isSubmitting}
                      InputProps={{
                        sx: {
                          color: '#FFFFFF',
                          backgroundColor: '#1A1A1A',
                          borderRadius: '12px',
                          '& fieldset': { borderColor: '#2D2D2D' }
                        }
                      }}
                      InputLabelProps={{ sx: { color: '#B3B3B3' } }}
                    />
                  )}
                  
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || !formData.name || !restaurantId  || (chatMode === 'human' && !isConnected)}
                    sx={{
                      backgroundColor: '#FFD700',
                      color: '#000000',
                      fontWeight: 700,
                      borderRadius: '12px',
                      py: 1.2,
                      '&:hover': { backgroundColor: '#FFE55C' }
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={24} sx={{ color: '#000000' }} />
                    ) : (
                      chatMode === 'ai' 
                        ? (language === 'ar' ? 'بدء المحادثة' : 'Start AI Chat')
                        : (language === 'ar' ? 'بدء المحادثة' : 'Start Chat')
                    )}
                  </Button>
                </Box>
              )}

              {/* Messages Area */}
              {!showStartForm && currentChat && (
                <>
                  <Box
                    ref={messagesContainerRef}
                    onScroll={handleScroll}
                    sx={{
                      flex: 1,
                      overflowY: 'auto',
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      backgroundColor: '#0D0D0D'
                    }}
                  >
                    {messages.map((msg, index) => {
                      const isUser = msg.sender_type === 'user';
                      const isAI = msg.sender_type === 'ai';
                      
                      return (
                        <Box
                          key={msg.id || index}
                          sx={{
                            display: 'flex',
                            justifyContent: isUser ? 'flex-end' : 'flex-start',
                            mb: 1
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: '85%',
                              backgroundColor: isUser 
                                ? '#FFD700' 
                                : isAI 
                                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                  : '#1A1A1A',
                              color: isUser ? '#000000' : '#FFFFFF',
                              borderRadius: '16px',
                              p: 1.5,
                              borderBottomRightRadius: isUser ? '4px' : '16px',
                              borderBottomLeftRadius: isUser ? '16px' : '4px',
                              boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                            }}
                          >
                            <Typography variant="body2" sx={{ wordBreak: 'break-word', fontSize: '0.85rem' }}>
                              {msg.message}
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 0.5,
                                mt: 0.5
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: isUser ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)',
                                  fontSize: '0.55rem'
                                }}
                              >
                                {formatTime(msg.created_at)}
                              </Typography>
                              {isUser && (
                                msg.is_read ? (
                                  <DoneAllIcon sx={{ fontSize: 10, color: 'rgba(0,0,0,0.6)' }} />
                                ) : (
                                  <CheckIcon sx={{ fontSize: 10, color: 'rgba(0,0,0,0.6)' }} />
                                )
                              )}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                    
                    {isTyping && chatMode === 'human' && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <Chip
                          label={language === 'ar' ? 'الدعم يكتب...' : 'Support is typing...'}
                          size="small"
                          sx={{
                            backgroundColor: '#1A1A1A',
                            color: '#B3B3B3',
                            animation: 'pulse 1.5s infinite'
                          }}
                        />
                      </Box>
                    )}
                    
                    {aiThinking && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <Chip
                          icon={<CircularProgress size={12} />}
                          label={language === 'ar' ? 'AI يفكر...' : 'AI is thinking...'}
                          size="small"
                          sx={{
                            backgroundColor: '#667eea',
                            color: '#FFFFFF'
                          }}
                        />
                      </Box>
                    )}
                    
                    <div ref={messagesEndRef} />
                  </Box>

                  {/* Scroll to bottom button */}
                  {showScrollButton && (
                    <IconButton
                      onClick={scrollToBottom}
                      sx={{
                        position: 'absolute',
                        bottom: 80,
                        right: 16,
                        backgroundColor: '#FFD700',
                        color: '#000000',
                        width: 36,
                        height: 36,
                        '&:hover': { backgroundColor: '#FFE55C' }
                      }}
                    >
                      <KeyboardArrowUpIcon />
                    </IconButton>
                  )}

                  {/* Suggested Questions for AI */}
                  {chatMode === 'ai' && messages.length < 3 && (
                    <Box sx={{ p: 1.5, borderTop: '1px solid #2D2D2D', backgroundColor: '#0D0D0D' }}>
                      <Typography variant="caption" sx={{ color: '#888888', mb: 1, display: 'block' }}>
                        {language === 'ar' ? 'أسئلة مقترحة:' : 'Suggested:'}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {suggestedQuestions[language === 'ar' ? 'ar' : 'en'].slice(0, 4).map((question, idx) => (
                          <Chip
                            key={idx}
                            label={question}
                            size="small"
                            onClick={() => {
                              setNewMessage(question);
                              handleAIChat(question);
                              setNewMessage('');
                            }}
                            sx={{
                              backgroundColor: '#1A1A1A',
                              color: '#FFD700',
                              border: '1px solid #2D2D2D',
                              fontSize: '0.7rem',
                              height: 28,
                              '&:hover': {
                                backgroundColor: '#2D2D2D',
                                cursor: 'pointer'
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Message Input */}
                  <Box
                    component="form"
                    onSubmit={handleSendMessage}
                    sx={{
                      p: 1.5,
                      borderTop: '1px solid #2D2D2D',
                      display: 'flex',
                      gap: 1,
                      backgroundColor: '#0D0D0D'
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={
                        chatMode === 'ai'
                          ? (language === 'ar' ? 'اسأل عن القائمة...' : 'Ask about menu...')
                          : (language === 'ar' ? 'اكتب رسالتك...' : 'Type message...')
                      }
                      value={newMessage}
                      onChange={handleTyping}
                      disabled={aiThinking}
                      InputProps={{
                        sx: {
                          color: '#FFFFFF',
                          backgroundColor: '#1A1A1A',
                          borderRadius: '24px',
                          '& fieldset': { borderColor: '#2D2D2D' },
                          '&:hover fieldset': { borderColor: '#FFD700' }
                        }
                      }}
                    />
                    <IconButton
                      type="submit"
                      disabled={!newMessage.trim() || aiThinking}
                      sx={{
                        backgroundColor: '#FFD700',
                        color: '#000000',
                        width: 40,
                        height: 40,
                        '&:hover': { backgroundColor: '#FFE55C' },
                        '&:disabled': { backgroundColor: '#2D2D2D', color: '#6B6B6B' }
                      }}
                    >
                      <SendIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </>
              )}
            </Box>
          )}
        </SwipeableDrawer>
      ) : (
        // Desktop version - keeps original design
        <Slide direction="up" in={chatOpen} mountOnEnter unmountOnExit>
          <Paper
            elevation={24}
            sx={{
              position: 'fixed',
              bottom: 90,
              right: 20,
              width: minimized ? 300 : 380,
              height: minimized ? 60 : 600,
              maxHeight: 'calc(100vh - 120px)',
              backgroundColor: '#0D0D0D',
              border: '1px solid #FFD700',
              borderRadius: '20px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 1300,
              transition: 'all 0.3s ease',
              boxShadow: '0 10px 40px rgba(255, 215, 0, 0.2)'
            }}
          >
            {/* Header - same as original */}
            <Box
              sx={{
                p: 1.5,
                background: 'linear-gradient(135deg, #FFD700 0%, #F5C800 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer'
              }}
              onClick={minimized ? toggleMinimize : undefined}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: '#000000', width: 32, height: 32 }}>
                  {chatMode === 'ai' ? (
                    <PsychologyIcon sx={{ color: '#FFD700', fontSize: 20 }} />
                  ) : (
                    <SupportIcon sx={{ color: '#FFD700', fontSize: 20 }} />
                  )}
                </Avatar>
                <Box>
                  <Typography sx={{ color: '#000000', fontWeight: 800, fontSize: '1rem' }}>
                    {chatMode === 'ai' 
                      ? (language === 'ar' ? 'مساعد QR Table الذكي' : 'QR Table AI Assistant')
                      : (language === 'ar' ? 'الدعم الفني' : 'Customer Support')}
                  </Typography>
                  {!minimized && (
                    <Typography sx={{ color: '#000000', fontSize: '0.7rem', opacity: 0.8 }}>
                      {chatMode === 'ai' 
                        ? (language === 'ar' ? '🤖 متاح 24/7' : '🤖 Available 24/7')
                        : (isConnected ? '🟢 Online' : '🔴 Offline')}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box>
                {!minimized && (
                  <ToggleButtonGroup
                    size="small"
                    value={chatMode}
                    exclusive
                    onChange={handleModeChange}
                    sx={{
                      mr: 1,
                      '& .MuiToggleButton-root': {
                        backgroundColor: '#0D0D0D',
                        color: '#000000',
                        border: 'none',
                        '&.Mui-selected': {
                          backgroundColor: '#000000',
                          color: '#FFD700'
                        }
                      }
                    }}
                  >
                    <ToggleButton value="human">
                      <Tooltip title={language === 'ar' ? 'دعم بشري' : 'Human Support'}>
                        <SupportIcon sx={{ fontSize: 18 }} />
                      </Tooltip>
                    </ToggleButton>
                    <ToggleButton value="ai">
                      <Tooltip title={language === 'ar' ? 'مساعد ذكي' : 'AI Assistant'}>
                        <PsychologyIcon sx={{ fontSize: 18 }} />
                      </Tooltip>
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMinimize();
                  }}
                  sx={{ color: '#000000' }}
                >
                  {minimized ? <ExpandLessIcon /> : <MinimizeIcon />}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleChat();
                  }}
                  sx={{ color: '#000000' }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Rest of desktop content - same as original but with scroll handling */}
            {!minimized && (
              <>
                {showStartForm && (
                  <Box
                    component="form"
                    onSubmit={handleStartChat}
                    sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto' }}
                  >
                    {/* Same start form content */}
                    <Typography sx={{ color: '#FFD700', textAlign: 'center', mb: 1 }}>
                      {chatMode === 'ai'
                        ? (language === 'ar' ? 'اسألني عن القائمة والعروض!' : 'Ask me about menu and offers!')
                        : (language === 'ar' ? 'كيف يمكننا مساعدتك؟' : 'How can we help you?')}
                    </Typography>
                    
                    <TextField
                      fullWidth
                      size="small"
                      label={language === 'ar' ? 'الاسم' : 'Name'}
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                      disabled={!!user || isSubmitting}
                      InputProps={{
                        sx: {
                          color: '#FFFFFF',
                          backgroundColor: '#1A1A1A',
                          '& fieldset': { borderColor: '#2D2D2D' },
                          '&:hover fieldset': { borderColor: '#FFD700' }
                        }
                      }}
                      InputLabelProps={{ sx: { color: '#B3B3B3' } }}
                    />
                    
                    <TextField
                      fullWidth
                      size="small"
                      label={language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      disabled={!!user || isSubmitting}
                      InputProps={{
                        sx: {
                          color: '#FFFFFF',
                          backgroundColor: '#1A1A1A',
                          '& fieldset': { borderColor: '#2D2D2D' },
                          '&:hover fieldset': { borderColor: '#FFD700' }
                        }
                      }}
                      InputLabelProps={{ sx: { color: '#B3B3B3' } }}
                    />
                    
                    {chatMode === 'human' && (
                      <TextField
                        fullWidth
                        size="small"
                        label={language === 'ar' ? 'الرسالة (اختياري)' : 'Message (optional)'}
                        multiline
                        rows={3}
                        value={formData.message}
                        onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                        disabled={isSubmitting}
                        InputProps={{
                          sx: {
                            color: '#FFFFFF',
                            backgroundColor: '#1A1A1A',
                            '& fieldset': { borderColor: '#2D2D2D' },
                            '&:hover fieldset': { borderColor: '#FFD700' }
                          }
                        }}
                        InputLabelProps={{ sx: { color: '#B3B3B3' } }}
                      />
                    )}
                    
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting || !formData.name || !restaurantId || (chatMode === 'human' && !isConnected)}
                      sx={{
                        backgroundColor: '#FFD700',
                        color: '#000000',
                        fontWeight: 700,
                        '&:hover': { backgroundColor: '#FFE55C' },
                        '&:disabled': { backgroundColor: '#2D2D2D', color: '#6B6B6B' }
                      }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={24} sx={{ color: '#000000' }} />
                      ) : (
                        chatMode === 'ai' 
                          ? (language === 'ar' ? 'بدء المحادثة مع AI' : 'Start AI Chat')
                          : (language === 'ar' ? 'بدء المحادثة' : 'Start Chat')
                      )}
                    </Button>
                  </Box>
                )}

                {!showStartForm && currentChat && (
                  <>
                    <Box
                      ref={messagesContainerRef}
                      onScroll={handleScroll}
                      sx={{
                        flex: 1,
                        overflowY: 'auto',
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        backgroundColor: '#0D0D0D'
                      }}
                    >
                      {messages.map((msg, index) => {
                        const isUser = msg.sender_type === 'user';
                        const isAI = msg.sender_type === 'ai';
                        
                        return (
                          <Box
                            key={msg.id || index}
                            sx={{
                              display: 'flex',
                              justifyContent: isUser ? 'flex-end' : 'flex-start',
                              mb: 1
                            }}
                          >
                            <Box
                              sx={{
                                maxWidth: '80%',
                                backgroundColor: isUser 
                                  ? '#FFD700' 
                                  : isAI 
                                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                    : '#1A1A1A',
                                color: isUser ? '#000000' : '#FFFFFF',
                                borderRadius: '12px',
                                p: 1.5,
                                borderBottomRightRadius: isUser ? '4px' : '12px',
                                borderBottomLeftRadius: isUser ? '12px' : '4px',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                              }}
                            >
                              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                {msg.message}
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-end',
                                  gap: 0.5,
                                  mt: 0.5
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: isUser ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)',
                                    fontSize: '0.6rem'
                                  }}
                                >
                                  {formatTime(msg.created_at)}
                                </Typography>
                                {isUser && (
                                  msg.is_read ? (
                                    <DoneAllIcon sx={{ fontSize: 12, color: 'rgba(0,0,0,0.6)' }} />
                                  ) : (
                                    <CheckIcon sx={{ fontSize: 12, color: 'rgba(0,0,0,0.6)' }} />
                                  )
                                )}
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                      
                      {isTyping && chatMode === 'human' && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                          <Chip
                            label={language === 'ar' ? 'الدعم يكتب...' : 'Support is typing...'}
                            size="small"
                            sx={{
                              backgroundColor: '#1A1A1A',
                              color: '#B3B3B3',
                              animation: 'pulse 1.5s infinite'
                            }}
                          />
                        </Box>
                      )}
                      
                      {aiThinking && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                          <Chip
                            icon={<CircularProgress size={12} />}
                            label={language === 'ar' ? 'AI يفكر...' : 'AI is thinking...'}
                            size="small"
                            sx={{
                              backgroundColor: '#667eea',
                              color: '#FFFFFF'
                            }}
                          />
                        </Box>
                      )}
                      
                      <div ref={messagesEndRef} />
                    </Box>

                    {/* Scroll button for desktop */}
                    {showScrollButton && (
                      <IconButton
                        onClick={scrollToBottom}
                        sx={{
                          position: 'absolute',
                          bottom: 80,
                          right: 16,
                          backgroundColor: '#FFD700',
                          color: '#000000',
                          width: 32,
                          height: 32,
                          '&:hover': { backgroundColor: '#FFE55C' }
                        }}
                      >
                        <KeyboardArrowUpIcon />
                      </IconButton>
                    )}

                    {chatMode === 'ai' && messages.length < 3 && (
                      <Box sx={{ p: 1.5, borderTop: '1px solid #2D2D2D', backgroundColor: '#0D0D0D' }}>
                        <Typography variant="caption" sx={{ color: '#888888', mb: 1, display: 'block' }}>
                          {language === 'ar' ? 'أسئلة مقترحة:' : 'Suggested questions:'}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {suggestedQuestions[language === 'ar' ? 'ar' : 'en'].map((question, idx) => (
                            <Chip
                              key={idx}
                              label={question}
                              size="small"
                              onClick={() => {
                                setNewMessage(question);
                                handleAIChat(question);
                                setNewMessage('');
                              }}
                              sx={{
                                backgroundColor: '#1A1A1A',
                                color: '#FFD700',
                                border: '1px solid #2D2D2D',
                                '&:hover': {
                                  backgroundColor: '#2D2D2D',
                                  cursor: 'pointer'
                                }
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    <Box
                      component="form"
                      onSubmit={handleSendMessage}
                      sx={{
                        p: 1.5,
                        borderTop: '1px solid #2D2D2D',
                        display: 'flex',
                        gap: 1,
                        backgroundColor: '#0D0D0D'
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={
                          chatMode === 'ai'
                            ? (language === 'ar' ? 'اسأل عن القائمة، العروض، أو أي شيء...' : 'Ask about menu, offers, or anything...')
                            : (language === 'ar' ? 'اكتب رسالتك...' : 'Type your message...')
                        }
                        value={newMessage}
                        onChange={handleTyping}
                        disabled={aiThinking}
                        InputProps={{
                          sx: {
                            color: '#FFFFFF',
                            backgroundColor: '#1A1A1A',
                            borderRadius: '20px',
                            '& fieldset': { borderColor: '#2D2D2D' },
                            '&:hover fieldset': { borderColor: '#FFD700' }
                          }
                        }}
                      />
                      <IconButton
                        type="submit"
                        disabled={!newMessage.trim() || aiThinking}
                        sx={{
                          backgroundColor: '#FFD700',
                          color: '#000000',
                          width: 40,
                          height: 40,
                          '&:hover': { backgroundColor: '#FFE55C' },
                          '&:disabled': { backgroundColor: '#2D2D2D', color: '#6B6B6B' }
                        }}
                      >
                        <SendIcon />
                      </IconButton>
                    </Box>
                  </>
                )}
              </>
            )}
          </Paper>
        </Slide>
      )}
      
      {/* Invitation Notification - Mobile Responsive */}
      {pendingGameInvitation && !hasAccepted && (
        <Alert 
          severity="info"
          sx={{ 
            position: 'fixed', 
            bottom: isMobile ? 160 : 160, 
            left: '50%', 
            transform: 'translateX(-50%)',
            zIndex: 1400,
            backgroundColor: '#0D0D0D',
            border: '1px solid #FFD700',
            color: '#FFFFFF',
            width: isMobile ? '90%' : 'auto',
            minWidth: isMobile ? 'auto' : 280,
            maxWidth: isMobile ? '90%' : 'auto',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            borderRadius: '16px'
          }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                size="small" 
                onClick={acceptInvitationHandler} 
                sx={{ 
                  color: '#4CAF50',
                  fontWeight: 700,
                  fontSize: isMobile ? '0.7rem' : '0.8rem',
                  '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' }
                }}
              >
                Accept
              </Button>
              <Button 
                size="small" 
                onClick={rejectInvitationHandler} 
                sx={{ 
                  color: '#FF3D00',
                  fontWeight: 700,
                  fontSize: isMobile ? '0.7rem' : '0.8rem',
                  '&:hover': { backgroundColor: 'rgba(255, 61, 0, 0.1)' }
                }}
              >
                Reject
              </Button>
            </Box>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
            🎮 {pendingGameInvitation.fromName} invited you to play Tic Tac Toe!
          </Typography>
        </Alert>
      )}
      

      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </>
  );
};

export default ChatWidget;